// src/MainLayout.jsx
import { Routes, Route, Navigate, useMatch } from 'react-router-dom';
import React, { useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useSidebar } from './contexts/SidebarContext';
import { IngredientLinesProvider } from "./contexts/IngredientLinesContext";

import TopNavigation from './components/TopNav/TopNavigation';
import SidebarLeft from './components/SidebarLeft';
import SidebarRight from './components/SidebarRight';
import SlideOver from './components/Shared/SlideOver';
import BottomDrawer from './components/BottomDrawer';
import CommunityPanel from './components/CommunityPanel';
import FloatingFAB from './components/FloatingWidgets';

import RecipeEditor from './components/Canvas/RecipeEditor';
import PreviewCard from './components/Canvas/PreviewCard';
import EditorLayout from './components/Canvas/EditorLayout';
import PlannerEditor from './pages/PlannerEditor';


import Dashboard from './pages/dashboard';
import PlannerPage from './pages/planner';
import CommunityPage from './pages/community';
import TemplatePickerPage from './pages/templates';
import RNDModePage from './pages/rnd';
import SettingsPage from './pages/settings';
import BlankCardsPage from './pages/admin/BlankCards';
import AdminRoute from './components/Shared/AdminRoute';
import LearningModal from './components/FloatingWidgets/LearningModal';
import CertificationProgress from './components/FloatingWidgets/CertificationProgress';



import './styles/globals.css';

function IngredientLinesGate({ children }) {
  const mEdit = useMatch('/recipes/:id/edit');
  const mNew = useMatch('/recipes/new/edit');
  const mPrint = useMatch('/recipes/:id/print');

  const recipeId = mEdit?.params?.id || mPrint?.params?.id || (mNew ? 'new' : null);

  if (!recipeId) return children;

  return (
    <IngredientLinesProvider recipeId={recipeId}>
      {children}
    </IngredientLinesProvider>
  );
}

export default function App() {
  const {
    libraryOpen,
    openLibrary,
    closeLibrary,
    utilitiesOpen,
    openUtilities,
    closeUtilities,
    bottomDrawerOpen,

  } = useSidebar();

  const [showCommunity, setShowCommunity] = useState(false);
  const [showLearning, setShowLearning] = useState(false);
  const [showCerts, setShowCerts] = useState(false);
  const openCommunity = () => setShowCommunity(true);
  const closeCommunity = () => setShowCommunity(false);

  return (
    <IngredientLinesGate>
      <>
      <TopNavigation
        onOpenLibrary={openLibrary}
        onOpenUtilities={openUtilities}
      />

      <div className="flex flex-col h-screen pt-14 md:pt-16 w-full overflow-x-hidden">
        {/* top padding = 56px mobile, 64px tablet+ to clear fixed header */}
        <div className="flex flex-1 overflow-hidden">
          <aside className="hidden md:block md:w-[280px] lg:w-[300px] xl:w-[320px] bg-gray-50 border-r overflow-auto">
            <SidebarLeft />
          </aside>

          <main className="flex-1 bg-white overflow-auto">
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/recipes/new/edit" element={<EditorLayout><RecipeEditor /></EditorLayout>} />
              <Route path="/recipes/:id/edit" element={<EditorLayout><RecipeEditor /></EditorLayout>} />
              <Route path="/recipes/:id/print" element={<EditorLayout><PreviewCard /></EditorLayout>} />
              <Route path="/recipes/:id/print" element={<PreviewCard />} />
              <Route path="/planner" element={<PlannerPage />} />
              <Route path="/planner/:id" element={<PlannerEditor />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/templates" element={<TemplatePickerPage />} />
              <Route path="/rnd" element={<RNDModePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin/blank-cards" element={<AdminRoute><BlankCardsPage /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>

          <aside className="hidden lg:block lg:w-[300px] xl:w-[320px] bg-gray-50 border-l overflow-auto">
            <SidebarRight />
          </aside>
        </div>

        {/* 📅 Bottom Planning Panel (Context-aware) */}
      <footer
        className={`
          transition-all duration-300 border-t shadow-inner bg-gray-50
          ${bottomDrawerOpen
              // mobile        tablet      small-desktop      large-desktop
              ? 'h-[min(200px,40vh)] sm:h-[min(240px,40vh)] lg:h-[min(280px,35vh)] xl:h-[min(320px,45vh)]'
              : 'h-[56px]'} 
          sm:h-[64px]
        `}
      >
        <BottomDrawer />
      </footer>
      </div>

      {/* 🎓 Floating Widgets (Responsive Positioning) */}
      <div
        className={`
          fixed z-50
          bottom-[80px] right-4 sm:bottom-4 sm:right-4
          max-w-[90vw] sm:max-w-xs
        `}
      >
        <FloatingFAB          
            onOpenCommunity={openCommunity}
            onOpenLearning={() => setShowLearning(true)}
            onOpenCerts={() => setShowCerts(true)} />
      </div>


      {/* 🧑‍🍳 Community Panel Modal */}
      {showCommunity && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-4xl h-[80vh] sm:rounded-lg shadow-xl overflow-y-auto relative">
            <CommunityPanel />
            <button
              onClick={closeCommunity}
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {showLearning && (
        <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-3xl h-[75vh] sm:rounded-lg shadow-xl overflow-y-auto relative">
            <LearningModal />
            <button
              onClick={() => setShowLearning(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {showCerts && (
        <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-lg h-[60vh] sm:rounded-lg shadow-xl overflow-y-auto relative">
            <CertificationProgress />
            <button
              onClick={() => setShowCerts(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
              ✕
            </button>
          </div>
        </div>
      )}



      {/* SlideOvers for mobile */}
      <SlideOver side="left" open={libraryOpen} onClose={closeLibrary}>
        <SidebarLeft />
      </SlideOver>

      <SlideOver side="right" open={utilitiesOpen} onClose={closeUtilities}>
        <SidebarRight />
      </SlideOver>

            {/* GLOBAL SLIDE-OVER CLOSE: left panel */}
      {libraryOpen && (
        <button
          onClick={closeLibrary}
          className="
            fixed top-4 right-4 z-50
            p-3 bg-white bg-opacity-90 rounded-full shadow-md
            focus:outline-none focus-visible:ring-2 focus-visible:ring-rich-gold
          "
          aria-label="Close library panel"
        >
          <ChevronLeftIcon className="h-6 w-6 text-charcoal" />
        </button>
      )}

      {/* GLOBAL SLIDE-OVER CLOSE: right panel */}
      {utilitiesOpen && (
        <button
          onClick={closeUtilities}
          className="
            fixed top-4 left-4 z-50
            p-3 bg-white bg-opacity-90 rounded-full shadow-md
            focus:outline-none focus-visible:ring-2 focus-visible:ring-rich-gold
          "
          aria-label="Close utilities panel"
        >
          <ChevronRightIcon className="h-6 w-6 text-charcoal" />
        </button>
      )}

      </>
    </IngredientLinesGate>
  );
}

